// Start here client (plan §4 /start and /admin/induction, spec §5.8, J6; T33b). A person reads and
// changes only their own progress; the completion table, due dates and the Delegate Training Log
// export are the owner's (induction.manage). Its own factory over the shared http layer.
import { API_BASE_PATH, createHttp, type HttpOptions } from './http.ts';
import { pathSegment } from './paths.ts';
import {
  completionPageSchema,
  inductionProgressSchema,
  lessonListSchema,
  lessonSchema,
  quizAttemptSchema,
  quizResultSchema,
  type CompletionPage,
  type InductionProgress,
  type Lesson,
  type LessonList,
  type QuizAnswer,
  type QuizAttempt,
  type QuizResult,
} from './schemas/induction.ts';

export * from './schemas/induction.ts';
// Lane I owns one export line in the package index; the tracker import client rides on it.
export * from './imports.ts';

export interface InductionPaging {
  readonly page?: number;
  readonly pageSize?: number;
}

export interface InductionApi {
  /** GET /start/progress: my lessons, quiz status, due date and completion. */
  progress(signal?: AbortSignal): Promise<InductionProgress>;
  /** GET /start/lessons: the lesson list with my read marks and where to resume. */
  lessons(signal?: AbortSignal): Promise<LessonList>;
  /** GET /start/lessons/:no: one lesson as a block tree, with prev and next. */
  lesson(no: number, signal?: AbortSignal): Promise<Lesson>;
  /** POST /start/lessons/:no/read: idempotent; returns my progress after the mark. */
  markRead(no: number): Promise<InductionProgress>;
  /** POST /start/quiz/attempts: 409 until every lesson is read. No answer key in the answer. */
  startAttempt(opts: { readonly idempotencyKey: string }): Promise<QuizAttempt>;
  /** PUT /start/quiz/attempts/:id with If-Match: scored once; a second submit is 409. */
  submitAttempt(
    attempt: { readonly id: number; readonly version: number },
    answers: readonly QuizAnswer[],
  ): Promise<QuizResult>;
  /** GET /admin/induction: the owner's completion table. */
  completionTable(paging?: InductionPaging, signal?: AbortSignal): Promise<CompletionPage>;
  /** PATCH /admin/induction/:userId with If-Match (0 while the due date is the default). */
  setDueDate(
    person: { readonly userId: number; readonly version: number },
    dueOn: string,
  ): Promise<InductionProgress>;
  /** The Delegate Training Log CSV, for a plain download link (the cookie goes with it). */
  trainingLogUrl(): string;
}

function pagingQuery(paging: InductionPaging): string {
  const params = new URLSearchParams();
  if (paging.page !== undefined) params.set('page', String(paging.page));
  if (paging.pageSize !== undefined) params.set('pageSize', String(paging.pageSize));
  const qs = params.toString();
  return qs === '' ? '' : `?${qs}`;
}

export function createInductionApi(options: HttpOptions = {}): InductionApi {
  const http = createHttp(options);
  const base = options.baseUrl ?? API_BASE_PATH;
  return {
    progress: (signal) =>
      http.json({ method: 'GET', path: '/start/progress', signal }, inductionProgressSchema),
    lessons: (signal) =>
      http.json({ method: 'GET', path: '/start/lessons', signal }, lessonListSchema),
    lesson: (no, signal) =>
      http.json({ method: 'GET', path: `/start/lessons/${pathSegment(no)}`, signal }, lessonSchema),
    markRead: (no) =>
      http.json(
        { method: 'POST', path: `/start/lessons/${pathSegment(no)}/read` },
        inductionProgressSchema,
      ),
    startAttempt: ({ idempotencyKey }) =>
      http.json(
        { method: 'POST', path: '/start/quiz/attempts', idempotencyKey },
        quizAttemptSchema,
      ),
    submitAttempt: (attempt, answers) =>
      http.json(
        {
          method: 'PUT',
          path: `/start/quiz/attempts/${pathSegment(attempt.id)}`,
          version: attempt.version,
          body: { answers },
        },
        quizResultSchema,
      ),
    completionTable: (paging = {}, signal) =>
      http.json(
        { method: 'GET', path: `/admin/induction${pagingQuery(paging)}`, signal },
        completionPageSchema,
      ),
    setDueDate: (person, dueOn) =>
      http.json(
        {
          method: 'PATCH',
          path: `/admin/induction/${pathSegment(person.userId)}`,
          version: person.version,
          body: { dueOn },
        },
        inductionProgressSchema,
      ),
    trainingLogUrl: () => `${base}/admin/induction/export`,
  };
}
